import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Heading from "../../components/common/Typography/Heading";
import { PUBLIC_SECTION_PADDING_TOP_CLASS } from "../Public/shared/publicLayoutSpec";

gsap.registerPlugin(ScrollTrigger);

const MessagingSystemLayout = () => {
  const sectionRef = useRef(null);
  const bgPinRef = useRef(null);
  const contentRef = useRef(null);
  const pointsRef = useRef([]);
  const addToPointsRef = (el) => {
    if (el && !pointsRef.current.includes(el)) {
      pointsRef.current.push(el);
    }
  };

  useEffect(() => {
    let pointTriggers = [];
    let pinST = null;
    const points = pointsRef.current;
    const section = sectionRef.current;
    const bgPin = bgPinRef.current;
    const content = contentRef.current;
    if (!section || !bgPin || !content) return;

    function setupPointTriggers() {
      if (!pinST || typeof pinST.start !== "number" || typeof pinST.end !== "number") return;
      // Remove old triggers
      pointTriggers.forEach(t => t.kill());
      pointTriggers = [];
      gsap.set(points, { opacity: 0, y: 50 });
      const numPoints = points.length;
      // Use pinST.start and pinST.end for absolute scroll positions
      const pinStart = pinST.start;
      const pinEnd = pinST.end;
      const pinDuration = pinEnd - pinStart;
      points.forEach((point, i) => {
        const segStart = pinStart + (i * pinDuration) / numPoints;
        const segEnd = pinStart + ((i + 1) * pinDuration) / numPoints;
        const trigger = gsap.to(point, {
          opacity: 1,
          y: 0,
          ease: "power2.out",
          scrollTrigger: {
            start: segStart,
            end: segEnd,
            scrub: true,
            invalidateOnRefresh: true,
          }
        });
        pointTriggers.push(trigger.scrollTrigger);
      });
    }

    // Pin the background image wrapper from where the image starts
    pinST = ScrollTrigger.create({
      trigger: bgPin,
      start: "top top",
      end: "+=180%", // pin for 1.8x the height of the image (longer pin)
      pin: bgPin,
      anticipatePin: 1,
      scrub: false,
    });

    // Initial setup
    setupPointTriggers();
    ScrollTrigger.addEventListener("refresh", setupPointTriggers);
    ScrollTrigger.refresh();

    // Cleanup
    return () => {
      pinST.kill();
      pointTriggers.forEach(t => t.kill());
      ScrollTrigger.removeEventListener("refresh", setupPointTriggers);
    };
  }, []);

  return (
    <div ref={sectionRef} className="relative w-full overflow-hidden">
      <div className={`bg-pastalblue ${PUBLIC_SECTION_PADDING_TOP_CLASS}`}>
        <div className="tp-container-home mx-auto">
          <div className="pb-12">
            <Heading variant={"h2"} className={"text-offblack"}>
              Manage your business with ease
            </Heading>
          </div>
        </div>
      </div>
      {/* Pin this background image wrapper */}
      <div ref={bgPinRef} className="w-full h-screen relative">
        <img
          src={require("../../assets/img/contact-us.jpg")}
          className="w-full h-full object-cover absolute inset-0 scale-x-[-1]"
          alt=""
        />
        <div className="absolute inset-0">
          <div ref={contentRef} className="relative tp-container mx-auto h-full">
            <div className="tp-container-small mx-auto flex justify-center sm:justify-start h-full items-center">
              <div className="flex flex-col text-white w-[420px] space-y-7 sm:space-y-10 xl:space-y-20 2xl:space-y-[60px]">
                <div className="flex flex-col space-y-7 sm:space-y-10 xl:space-y-20 2xl:space-y-[40px] w-full">
                  <div ref={addToPointsRef} className="flex flex-row items-start space-x-4">
                    <div className="flex-none" style={{ height: '20px', width: '5px', background: 'white', borderRadius: 0, marginTop: '6px', flexShrink: 0, display: 'block' }}></div>
                    <div className="flex flex-col space-y-1">
                    <Heading
                        variant={"h5"}
                        className={"font-bold !text-white"}
                      >Secure Messaging. Manage all customer inquiries in one place, linked to each post.</Heading>
                    </div>
                  </div>
                  <div ref={addToPointsRef} className="flex flex-row items-start space-x-4">
                    <div className="flex-none" style={{ height: '20px', width: '5px', background: 'white', borderRadius: 0, marginTop: '6px', flexShrink: 0, display: 'block' }}></div>
                    <div className="flex flex-col space-y-1">
                    <Heading
                        variant={"h5"}
                        className={"font-bold !text-white"}
                      >Profile Management. Update your business details, service info, licences, and portfolio.</Heading>
                    </div>
                  </div>
                  <div ref={addToPointsRef} className="flex flex-row items-start space-x-4">
                    <div className="flex-none" style={{ height: '20px', width: '5px', background: 'white', borderRadius: 0, marginTop: '6px', flexShrink: 0, display: 'block' }}></div>
                    <div className="flex flex-col space-y-1">
                    <Heading
                        variant={"h5"}
                        className={"font-bold !text-white"}
                      >Performance Metrics. Track views, engagement, suburb interest, and keyword activity.</Heading>
                    </div>
                  </div>
                  <div ref={addToPointsRef} className="flex flex-row items-start space-x-4">
                    <div className="flex-none" style={{ height: '20px', width: '5px', background: 'white', borderRadius: 0, marginTop: '6px', flexShrink: 0, display: 'block' }}></div>
                    <div className="flex flex-col space-y-1">
                    <Heading
                        variant={"h5"}
                        className={"font-bold !text-white"}
                      >Compliance Centre. Upload and manage trade licences, insurance, and platform badges.</Heading>
                    </div>
                  </div>
                  <div ref={addToPointsRef} className="flex flex-row items-start space-x-4">
                    <div className="flex-none" style={{ height: '20px', width: '5px', background: 'white', borderRadius: 0, marginTop: '6px', flexShrink: 0, display: 'block' }}></div>
                    <div className="flex flex-col space-y-1">
                    <Heading
                        variant={"h5"}
                        className={"font-bold !text-white"}
                      >Post Management. Create, edit, and monitor all listings with plan-based controls.</Heading>
                    </div>
                  </div>
                  <div ref={addToPointsRef} className="flex flex-row items-start space-x-4">
                    <div className="flex-none" style={{ height: '20px', width: '5px', background: 'white', borderRadius: 0, marginTop: '6px', flexShrink: 0, display: 'block' }}></div>
                    <div className="flex flex-col space-y-1">
                    <Heading
                        variant={"h5"}
                        className={"font-bold !text-white"}
                      >Ongoing Enhancements. New tools are on the way to improve performance and compliance</Heading>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagingSystemLayout;
